/**
 * Step ledger. Screen spec 4.9, Instrument motif 5.
 *
 * THE ABSOLUTE RULE: never a bare spinner, ever. If we do not know what step
 * we are on, we say what we are waiting for. There is no spinner in this
 * product and no percentage we cannot honor.
 *
 * Every step is visible from the start, none are hidden, none reorder, and
 * completed steps never disappear. On failure the completed steps stay on
 * screen and are explicitly acknowledged in the copy, because "your database
 * was set up and then the build stopped" is a materially different sentence
 * from "it failed".
 *
 * The state glyph sits in a fixed 24px column so labels align and never jump
 * when a step changes, and the elapsed time sits in a mono column on the
 * right. The vertical hairline through the glyph column is the spine: it is
 * what makes five separate rows read as one operation.
 *
 * Work in flight is neutral and moving, never colored. Colored motion reads as
 * an alarm, and a build going fine is not an alarm.
 *
 * Split out of AsyncNarration.tsx so a real (non-mock) caller can render this
 * without pulling lib/mock/* into the module graph.
 */

import type { ReactNode } from 'react';
import { Lamp } from './StatusPill';
import { t } from '../../lib/i18n/t';
import type { StepStatus } from '../../lib/types';
import '../../styles/components/narration.css';

/** People must be told they are allowed to walk away. Ninety real seconds. */
export const LONG_RUNNING_MS = 90_000;

export interface NarrationStep {
  label: string;
  status: StepStatus;
  /** Mono, right aligned. Shown on a step that has a duration to report. */
  elapsed?: string;
  /** An optional mono detail in parentheses on a succeeded step. */
  detail?: string;
}

function StepGlyph({ status }: { status: StepStatus }) {
  if (status === 'running') return <Lamp value="deploying" size="md" />;

  return (
    <span className="narr__glyph" data-status={status} aria-hidden="true">
      {status === 'succeeded' ? (
        <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
          <path
            d="M3 8.5 6.5 12 13 4.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ) : null}
      {status === 'failed' ? (
        <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
          <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      ) : null}
      {status === 'pending' ? <span className="narr__dot" /> : null}
      {status === 'skipped' ? <span className="narr__dash" /> : null}
    </span>
  );
}

export interface StepLedgerProps {
  steps: NarrationStep[];
  /** The error block, mounted beneath the list when a step failed. */
  error?: ReactNode;
  /** Shows the leave this page line. Driven by real elapsed time. */
  longRunning?: boolean;
  onLeave?: () => void;
  /** Overrides the default long-running notice. Real mode has no email to promise. */
  longRunningNote?: string;
  /** Overrides the default "Leave this page" button label. */
  leaveLabel?: string;
}

export function StepLedger({
  steps,
  error,
  longRunning = false,
  onLeave,
  longRunningNote,
  leaveLabel,
}: StepLedgerProps) {
  const runningLabel = steps.find((step) => step.status === 'running')?.label;

  return (
    <div className="narr">
      <ol className="narr__list">
        {steps.map((step) => (
          <li className="narr__step" data-status={step.status} key={step.label}>
            <span className="narr__col">
              <StepGlyph status={step.status} />
            </span>
            <span className="narr__label">
              {step.label}
              {step.detail ? <span className="narr__detail mono"> ({step.detail})</span> : null}
            </span>
            <span className="narr__time mono">{step.elapsed ?? ''}</span>
          </li>
        ))}
      </ol>

      {/* One polite announcement for the whole operation, naming the step. A
          per row live region would read the entire list on every change. */}
      <span className="narr__say" role="status" aria-live="polite">
        {runningLabel ?? ''}
      </span>

      {error ? <div className="narr__error">{error}</div> : null}

      {longRunning ? (
        <p className="narr__long">
          {longRunningNote ?? t("This is taking longer than usual. You can leave this page, we'll email you.")}{' '}
          {onLeave ? (
            <button type="button" className="narr__leave" onClick={onLeave}>
              {leaveLabel ?? t('Leave this page')}
            </button>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
